import api from './api';
import type { PaginatedResponse } from './api';
import type { ApiWish, CreateWishRequest } from '../types';

/**
 * Get paginated list of wishes
 */
export const getWishes = async (params?: {
  category?: string;
  region?: string;
  urgent?: boolean;
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<ApiWish>> => {
  // Convert params to URL-compatible format (booleans must be strings)
  const queryParams: Record<string, string | number> = {};
  if (params?.category) queryParams.category = params.category;
  if (params?.region) queryParams.region = params.region;
  if (params?.urgent !== undefined) queryParams.urgent = params.urgent ? '1' : '0';
  if (params?.status) queryParams.status = params.status;
  if (params?.search) queryParams.search = params.search;
  if (params?.limit) queryParams.limit = params.limit;
  if (params?.offset !== undefined) queryParams.offset = params.offset;

  // API returns: { success: true, data: [...], pagination: {...} }
  // api.get() returns response with data already extracted: { success: true, data: [...], pagination: {...} }
  const response = await api.get<ApiWish[]>('/api/wishes', queryParams);

  if (response.success && response.data && response.pagination) {
    return {
      data: response.data,
      pagination: {
        total: response.pagination.total,
        limit: response.pagination.limit,
        offset: response.pagination.offset,
      },
    };
  }

  return {
    data: [],
    pagination: { total: 0, limit: 20, offset: 0 },
  };
};

/**
 * Get wish by ID
 */
export const getWishById = async (id: string): Promise<ApiWish | null> => {
  const response = await api.get<ApiWish>(`/api/wishes/${id}`);
  return response.success && response.data ? response.data : null;
};

/**
 * Create new wish
 */
export const createWish = async (data: CreateWishRequest): Promise<ApiWish | null> => {
  const response = await api.post<ApiWish>('/api/wishes', data);
  return response.success && response.data ? response.data : null;
};

/**
 * Update wish status
 */
export const updateWishStatus = async (
  id: string,
  status: 'open' | 'matched' | 'closed'
): Promise<ApiWish | null> => {
  const response = await api.patch<ApiWish>(`/api/wishes/${id}`, { status });
  return response.success && response.data ? response.data : null;
};

/**
 * Contact wish owner (requires authentication)
 */
export const contactWish = async (id: string): Promise<{ success: boolean; message?: string }> => {
  const response = await api.post<{ success: boolean }>(`/api/wishes/${id}/contact`, {});

  if (response.success) {
    return { success: true };
  }

  // Return specific error message from API
  return {
    success: false,
    message: response.error?.message || '聯繫失敗，請稍後再試。',
  };
};
